import { useEffect, useState } from "react";
import { Content as RadixDialogContent } from "@radix-ui/react-dialog";
import {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog.jsx";
import { Button } from "@/components/ui/button.jsx";
import { useSessionTenant } from "@/contexts/SessionTenantContext.jsx";
import { subscribeTenantChange } from "@/api/tenant-change-channel.js";

// Mirrors `DialogContent` from the wrapper but omits the close (X) button,
// since this dialog is blocking and must only be dismissed by refreshing.
const DIALOG_CONTENT_CLASS =
  "fixed left-[50%] top-[50%] z-modal grid w-full max-w-md translate-x-[-50%] translate-y-[-50%] gap-4 border border-border bg-card text-card-foreground p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg";

const preventClose = (e) => e.preventDefault();

/**
 * Shows a blocking refresh dialog when another same-origin OneView tab
 * announces (via `BroadcastChannel`) that it switched to a different tenant.
 */
export default function TenantChangeWatcher() {
  const { availableTenants } = useSessionTenant();
  const [externalTenantId, setExternalTenantId] = useState(null);

  useEffect(() => subscribeTenantChange(setExternalTenantId), []);

  if (!externalTenantId) return null;

  const newTenant = availableTenants.find(
    (t) => t.tenantId === externalTenantId
  );
  const newName = newTenant?.displayName || `tenant ${externalTenantId}`;

  return (
    <Dialog open modal>
      <DialogPortal>
        <DialogOverlay />
        <RadixDialogContent
          onEscapeKeyDown={preventClose}
          onPointerDownOutside={preventClose}
          onInteractOutside={preventClose}
          className={DIALOG_CONTENT_CLASS}>
          <DialogHeader>
            <DialogTitle className='text-lg font-semibold text-foreground'>
              Tenant Changed
            </DialogTitle>
            <DialogDescription className='text-sm text-muted-foreground mt-2'>
              {`The Tenant has been changed to `}
              <span className='font-medium text-foreground'>{`'${newName}'`}</span>
              {` in another tab. Refresh required, unsaved changes will be lost.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className='flex flex-row justify-end gap-2'>
            <Button
              onClick={() => window.location.assign("/")}
              className='font-normal text-sm bg-primary text-white hover:bg-primary/90'>
              Refresh
            </Button>
          </DialogFooter>
        </RadixDialogContent>
      </DialogPortal>
    </Dialog>
  );
}
